import {
  Calendar,
  CalendarClearDate,
  CalendarSelectDate,
  DotsMenu,
  Sunrise,
} from '@griply/assets';
import { Task, Timestamp, UpdateTask } from '@griply/types';
import { getWeekStartsOn } from '@griply/utils';
import { addDays, differenceInCalendarDays, endOfDay } from 'date-fns';
import { buildDateUpdateTasks, useUpdateTasks } from 'features/tasks';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Colors, IconButton } from 'shared/components/ui/icon-button';
import { QuickActionRow } from 'shared/components/ui/quick-action-row';
import { Tooltip } from 'shared/components/ui/tooltip';
import {
  MetaViewOptions,
  useOpenTaskDetail,
} from 'shared/contexts/task-detail';
import { useToday } from 'shared/contexts/today';
import { useUser } from 'shared/hooks/use-user';

export type TaskRescheduleQuickActionsProps = {
  task: Task;
  occurrenceDate: Timestamp;
  onSelectDate: () => void;
  onClose: () => void;
  onAction?: () => void;
  onUpdate?: (task: Task, updates: UpdateTask) => void;
};

export const TaskRescheduleQuickActions: React.FC<
  TaskRescheduleQuickActionsProps
> = ({ task, occurrenceDate, onSelectDate, onClose, onAction, onUpdate }) => {
  const { t } = useTranslation();
  const { submit: updateTasks } = useUpdateTasks();
  const openTaskDetail = useOpenTaskDetail();
  const today = useToday();
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);

  const tasks = useMemo(() => [task], [task]);

  const todayRef = useRef<HTMLButtonElement>(null);
  const tomorrowRef = useRef<HTMLButtonElement>(null);
  const selectDateRef = useRef<HTMLButtonElement>(null);
  const clearDateRef = useRef<HTMLButtonElement>(null);
  const upcomingRef = useRef<HTMLButtonElement>(null);

  const openUpcoming = () => {
    openTaskDetail(task, MetaViewOptions.Upcoming);
    onAction?.();
    onClose();
  };

  // The current value targets the opened occurrence, not the habit's first one.
  const applyDate = (date?: Timestamp) => {
    const updates = buildDateUpdateTasks(tasks, date, occurrenceDate, {
      weekStartsOn,
      today,
    });

    if (!updates.length) {
      onClose();
      return;
    }

    onUpdate
      ? updates.forEach((update) => onUpdate(task, update))
      : updateTasks(updates);

    onAction?.();
    onClose();
  };

  const setDateToday = () => applyDate(endOfDay(new Date()));
  const setDateTomorrow = () => applyDate(endOfDay(addDays(new Date(), 1)));
  const clearDate = () => applyDate(undefined);

  const daysFromToday = differenceInCalendarDays(occurrenceDate, today);
  const canRescheduleToToday = daysFromToday < 0;
  const canRescheduleToTomorrow = daysFromToday <= 0;

  return (
    <QuickActionRow label={t('context-menus.task.labels.reschedule')}>
      {canRescheduleToToday && (
        <>
          <IconButton
            ref={todayRef}
            icon={Calendar}
            onClick={setDateToday}
            color={Colors.Success}
            aria-label={t('forms.deadline.fields.today.label')}
          />
          <Tooltip
            referenceElement={todayRef}
            label={t('forms.deadline.fields.today.label')}
          />
        </>
      )}
      {canRescheduleToTomorrow && (
        <>
          <IconButton
            ref={tomorrowRef}
            icon={Sunrise}
            onClick={setDateTomorrow}
            color={Colors.Info}
            aria-label={t('forms.deadline.fields.tomorrow.label')}
          />
          <Tooltip
            referenceElement={tomorrowRef}
            label={t('forms.deadline.fields.tomorrow.label')}
          />
        </>
      )}
      <IconButton
        ref={selectDateRef}
        icon={CalendarSelectDate}
        onClick={onSelectDate}
        color={Colors.Inherit}
        aria-label={t('forms.deadline.fields.select-date.label')}
      />
      <Tooltip
        referenceElement={selectDateRef}
        label={t('forms.deadline.fields.select-date.label')}
      />
      <IconButton
        ref={clearDateRef}
        icon={CalendarClearDate}
        onClick={clearDate}
        color={Colors.Default}
        aria-label={t('context-menus.task.labels.timeslot.occurrence.remove')}
      />
      <Tooltip
        referenceElement={clearDateRef}
        label={t('context-menus.task.labels.timeslot.occurrence.remove')}
      />
      <IconButton
        ref={upcomingRef}
        icon={DotsMenu}
        onClick={openUpcoming}
        color={Colors.Default}
        aria-label={t('context-menus.task.labels.timeslot.occurrence.upcoming')}
      />
      <Tooltip
        referenceElement={upcomingRef}
        label={t('context-menus.task.labels.timeslot.occurrence.upcoming')}
      />
    </QuickActionRow>
  );
};
